import * as Yup from 'yup';
import { getMaxValidation, getRequiredMessage } from 'views/declarations/utils/validation-utils';
import { MAX_SCREENING_METHODS, MIN_SCREENING_METHODS } from './types';

type Context = { postcodeOptionalCountries?: string[] };

const isBlank = (value?: string | null) => !value?.trim();

export const validationSchema = Yup.object({
    screeningDetails: Yup.object({
        result: Yup.string().required(getRequiredMessage('screeningResult')).nullable(),
        methods: Yup.array()
            .of(Yup.string())
            .min(MIN_SCREENING_METHODS, `At least ${MIN_SCREENING_METHODS} screening methods are required.`)
            .max(MAX_SCREENING_METHODS, `No more than ${MAX_SCREENING_METHODS} screening methods are allowed.`)
            .test('distinct-methods', 'Each screening method may only be selected once.', (methods) => {
                const selected = (methods ?? []).filter(Boolean) as string[];
                return new Set(selected).size === selected.length;
            })
            .required(getRequiredMessage('screeningMethods')),
        code: getMaxValidation('code', 17).nullable(),
        notes: getMaxValidation('notes', 512).nullable(),
    }),

    authorizedPerson: Yup.object({
        typeOfPerson: Yup.string().nullable(),
        name: getMaxValidation('name', 70).nullable(),
        identificationNumber: getMaxValidation('identificationNumber', 17).nullable(),
    }).test('all-or-none', '', function (value) {
        const { typeOfPerson, name, identificationNumber } = value ?? {};
        if (isBlank(typeOfPerson) && isBlank(name) && isBlank(identificationNumber)) return true;

        const errors: Yup.ValidationError[] = [];
        if (isBlank(typeOfPerson))
            errors.push(this.createError({ path: `${this.path}.typeOfPerson`, message: getRequiredMessage('type') }));
        if (isBlank(name))
            errors.push(this.createError({ path: `${this.path}.name`, message: getRequiredMessage('name') }));
        if (isBlank(identificationNumber))
            errors.push(
                this.createError({
                    path: `${this.path}.identificationNumber`,
                    message: getRequiredMessage('identificationNumber'),
                })
            );

        return errors.length ? new Yup.ValidationError(errors) : true;
    }),

    facilityAddress: Yup.object({
        street: getMaxValidation('street', 70).nullable(),
        number: getMaxValidation('number', 35).nullable(),
        poBox: getMaxValidation('poBox', 70).nullable(),
        city: getMaxValidation('city', 35).nullable(),
        postCode: getMaxValidation('postCode', 17).nullable(),
        country: Yup.string().nullable(),
    }).test('address-rules', '', function (value) {
        const { street, number, poBox, city, postCode, country } = value ?? {};
        const touched = [street, number, poBox, city, postCode, country].some((field) => !isBlank(field));
        if (!touched) return true;

        const errors: Yup.ValidationError[] = [];
        const fail = (path: string, message: string) =>
            errors.push(this.createError({ path: `${this.path}.${path}`, message }));

        if (isBlank(city)) fail('city', getRequiredMessage('city'));
        if (isBlank(country)) fail('country', getRequiredMessage('country'));

        // R3002: an address is identified either by street and number, or by a P.O. box.
        if (isBlank(poBox) && (isBlank(street) || isBlank(number))) {
            fail('street', 'Provide either a street and number, or a P.O. box.');
        }

        // C3034: the postcode is mandatory unless the country only has locality names.
        const { postcodeOptionalCountries = [] } = (this.options.context ?? {}) as Context;
        const postcodeOptional = !country || postcodeOptionalCountries.includes(country);
        if (isBlank(postCode) && !postcodeOptional && postcodeOptionalCountries.length > 0) {
            fail('postCode', getRequiredMessage('postCode'));
        }

        return errors.length ? new Yup.ValidationError(errors) : true;
    }),
});
